import React, { useContext, useEffect, useState } from 'react';
import Layout from '../../elements/Layout';
import { IoIosSearch, IoMdOptions } from 'react-icons/io';
import { MdAdd, MdDeleteForever, MdFilterList, MdFileDownload } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { activateCustomer, fetchCustomers, purgeCustomer, updateCustomerStatus } from '../../utils/api/api';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { canAccess } from '../../utils/canAccess';
import { AuthContext } from '../../utils/context/AuthContext';
import { FaEye, FaUsers, FaMale, FaFemale, FaQuestionCircle, FaChevronLeft, FaChevronRight, FaEllipsisH } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const Customers = () => {
  const { user } = useContext(AuthContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [activeTab, setActiveTab] = useState("deactivate");
  const [customerStatus, setCustomerStatus] = useState("inactive");
  const [error, setError] = useState(null);
  const [stats, setStats] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [male, setMale] = useState(0);
  const [female, setFemale] = useState(0);
  const [unknownGender, setUnknownGender] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  useEffect(() => {
    if (customerStatus === "active") {
      setActiveTab("deactivate");
    } else {
      setActiveTab("activate");
    }
  }, [customerStatus]);

  const openActionModal = (id, status) => {
    setSelectedCustomerId(id);
    setCustomerStatus(status);
    setIsModalOpen(true);
  };

  const truncateText = (text, limit) => {
    if (!text) return '';
    return text.length > limit ? `${text.slice(0, limit)}...` : text;
  };

  useEffect(() => {
    if (stats) {
      stats.forEach(item => {
        if (item.gender === "Male") {
          setMale(item.total);
        } else if (item.gender === "Female") {
          setFemale(item.total);
        } else if (item.gender === null) {
          setUnknownGender(item.total);
        } else if (item.total_items) {
          setTotalItems(item.total_items);
        }
      });
    }
  }, [stats]);

  const [filters, setFilters] = useState({
    gender: '',
    status: '',
    city: '',
  });

  const getCustomers = async (page, size) => {
    setLoading(true);

    const payload = {
      page: page,
      pageSize: size,
      gender: filters.gender || undefined,
      search: searchQuery || undefined,
    };

    try {
      const data = await fetchCustomers(payload);
      setCustomers(data.customers);
      setFilteredCustomers(data.customers);
      setStats(data.stats);
      setTotalPages(data.meta.totalPages);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch customers.');
      setLoading(false);
    }
  };

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    getCustomers(currentPage, itemsPerPage);
  }, [currentPage, itemsPerPage, filters.gender, searchQuery]);

  const exportCustomersToPDF = (customers) => {
    const doc = new jsPDF();
    doc.text("Customer List", 14, 10);

    const tableColumn = ["#", "Name", "ID", "Phone Number", "Status"];
    const tableRows = customers.map((customer, index) => [
      index + 1,
      `${customer.first_name} ${customer.last_name}`,
      customer.id_number,
      customer.phone_number || "N/A",
      customer.status,
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    doc.save("Customers_List.pdf");
  };

  const exportCustomersToExcel = async () => {
    setLoading(true);

    const payload = {
      page: 1,
      pageSize: 1000,
      gender: filters.gender || undefined,
      search: searchQuery || undefined,
    };

    try {
      const data = await fetchCustomers(payload);
      const customers = data.customers;

      const worksheet = XLSX.utils.json_to_sheet(customers);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');

      XLSX.writeFile(workbook, 'Customers.xlsx');
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch customers.');
      setLoading(false);
    }
  };

  const deleteCustomer = async () => {
    setLoading(true);
    try {
      await purgeCustomer(selectedCustomerId);
      toast.success("Customer deleted successfully");
      setIsModalOpen(false);
      getCustomers(currentPage, itemsPerPage);
    } catch (error) {
      toast.error(error.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const deactivateCustomer = async () => {
    setLoading(true);
    try {
      await updateCustomerStatus(selectedCustomerId, { status: "inactive" });
      toast.success("Customer deactivated successfully");
      getCustomers(currentPage, itemsPerPage);
    } catch (error) {
      toast.error(error.message || "Request failed");
    } finally {
      setLoading(false);
      setIsModalOpen(false);
    }
  };

  const activateCustomerStatus = async () => {
    setLoading(true);
    try {
      await activateCustomer(selectedCustomerId, { status: "active" });
      toast.success("Customer activated successfully");
      getCustomers(currentPage, itemsPerPage);
    } catch (error) {
      toast.error(error.message || "Request failed");
    } finally {
      setLoading(false);
      setIsModalOpen(false);
    }
  };

  const handleExport = (type) => {
    if (type === "excel") {
      exportCustomersToExcel();
    } else if (type === "pdf") {
      exportCustomersToPDF(customers);
    }
    setIsExportOpen(false);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 24 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    },
    hover: {
      y: -5,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 15
      }
    },
    tap: {
      y: 0,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 15
      }
    }
  };

  return (
    <Layout text={"Customers"}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="space-y-6"
      >
        {/* Stats Cards */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-1 md:grid-cols-4 gap-6"
        >
          <motion.div
            whileHover="hover"
            whileTap="tap"
            variants={cardVariants}
            className="rounded-2xl overflow-hidden bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-sm"
          >
            <div className="relative h-full p-6">
              <div className="absolute top-0 right-0 opacity-10">
                <FaUsers size={80} className="text-yellow-500" />
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-yellow-500 p-3 rounded-xl shadow-md">
                  <FaUsers className="text-white text-xl" />
                </div>
                <h3 className="text-primary font-semibold">Total Customers</h3>
              </div>
              <div className="mt-auto">
                <p className="text-4xl font-bold text-yellow-600">{totalItems}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover="hover"
            whileTap="tap"
            variants={cardVariants}
            className="rounded-2xl overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 shadow-sm"
          >
            <div className="relative h-full p-6">
              <div className="absolute top-0 right-0 opacity-10">
                <FaMale size={80} className="text-blue-500" />
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-blue-500 p-3 rounded-xl shadow-md">
                  <FaMale className="text-white text-xl" />
                </div>
                <h3 className="text-primary font-semibold">Male Customers</h3>
              </div>
              <div className="mt-auto">
                <p className="text-4xl font-bold text-blue-600">{male}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover="hover"
            whileTap="tap"
            variants={cardVariants}
            className="rounded-2xl overflow-hidden bg-gradient-to-br from-pink-50 to-pink-100 shadow-sm"
          >
            <div className="relative h-full p-6">
              <div className="absolute top-0 right-0 opacity-10">
                <FaFemale size={80} className="text-pink-500" />
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-pink-500 p-3 rounded-xl shadow-md">
                  <FaFemale className="text-white text-xl" />
                </div>
                <h3 className="text-primary font-semibold">Female Customers</h3>
              </div>
              <div className="mt-auto">
                <p className="text-4xl font-bold text-pink-600">{female}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover="hover"
            whileTap="tap"
            variants={cardVariants}
            className="rounded-2xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 shadow-sm"
          >
            <div className="relative h-full p-6">
              <div className="absolute top-0 right-0 opacity-10">
                <FaQuestionCircle size={80} className="text-gray-500" />
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-gray-500 p-3 rounded-xl shadow-md">
                  <FaQuestionCircle className="text-white text-xl" />
                </div>
                <h3 className="text-primary font-semibold">Unknown Gender</h3>
              </div>
              <div className="mt-auto">
                <p className="text-4xl font-bold text-gray-600">{unknownGender}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Search and Controls */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white/80 backdrop-blur-sm p-5 rounded-2xl shadow-sm"
        >
          <div className="flex items-center gap-3 w-full md:w-auto">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="relative flex-1 md:w-80"
            >
              <input
                type="text"
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200"
              />
              <IoIosSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative"
            >
              <motion.button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <MdFilterList size={20} />
                <span className="hidden md:inline">Filters</span>
              </motion.button>

              <AnimatePresence>
                {isFilterOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 z-40 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 p-4"
                  >
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Filter Customers</h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Gender</label>
                        <select
                          value={filters.gender}
                          onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
                          className="w-full p-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">All</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                        <select
                          value={filters.status}
                          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                          className="w-full p-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">All</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>

                      <div className="flex justify-end">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setFilters({ gender: '', status: '', city: '' });
                            setIsFilterOpen(false);
                          }}
                          className="px-3 py-1.5 text-xs text-gray-700 bg-gray-100 rounded-lg mr-2"
                        >
                          Reset
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setIsFilterOpen(false)}
                          className="px-3 py-1.5 text-xs text-white bg-secondary rounded-lg"
                        >
                          Apply
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto justify-end">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Show:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
                className="p-2 bg-gray-50 border border-gray-200 rounded-xl text-sm"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative"
            >
              <motion.button
                onClick={() => setIsExportOpen(!isExportOpen)}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
              >
                <MdFileDownload size={20} />
                <span>Export</span>
              </motion.button>

              <AnimatePresence>
                {isExportOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 z-40 mt-2 w-40 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                  >
                    <motion.button
                      whileHover={{ backgroundColor: "#f3f4f6" }}
                      onClick={() => handleExport("excel")}
                      className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                    >
                      Export to Excel
                    </motion.button>
                    <motion.button
                      whileHover={{ backgroundColor: "#f3f4f6" }}
                      onClick={() => handleExport("pdf")}
                      className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Export to PDF
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {canAccess(user, 'create_customer') && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard/customers/add-new')}
                className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-white rounded-xl hover:bg-secondary/90 transition-colors"
              >
                <MdAdd size={20} />
                <span>Add Customer</span>
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Customers Table */}
        <motion.div
          variants={itemVariants}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
        >
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full"
              />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <FaUsers size={60} className="text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No customers found</p>
              <p className="text-gray-400 text-sm mt-2">Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredCustomers.map((customer, index) => (
                      <motion.tr
                        key={customer.id}
                        whileHover={{ backgroundColor: "#f9fafb" }}
                        className="group bg-white"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {index + 1 + (currentPage - 1) * itemsPerPage}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0 bg-secondary/10 text-secondary rounded-full flex items-center justify-center">
                              {customer?.first_name?.[0]}{customer?.last_name?.[0]}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {truncateText(`${customer?.first_name} ${customer?.last_name}`, 25)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {customer?.email || 'No email provided'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {customer?.id_number || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {customer?.phone_number || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${customer?.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                            }`}>
                            {customer?.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {canAccess(user, 'view_customer') && (
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => navigate(`/dashboard/customers/${customer.id}/view`)}
                                className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                              >
                                <FaEye size={16} />
                              </motion.button>
                            )}
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => openActionModal(customer.id, customer.status)}
                              className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              <MdDeleteForever size={18} />
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 0 && (
                <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                  <div className="flex-1 flex justify-between items-center">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-xl ${currentPage === 1
                          ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                          : 'text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                    >
                      <FaChevronLeft className="mr-2 h-4 w-4" />
                      Previous
                    </motion.button>

                    <div className="hidden md:flex gap-1">
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;

                        // Always show first page, last page, current page, and neighbors
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <motion.button
                              key={pageNumber}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => paginate(pageNumber)}
                              className={`relative inline-flex items-center justify-center w-10 h-10 rounded-xl text-sm font-medium ${currentPage === pageNumber
                                  ? 'bg-secondary text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                              {pageNumber}
                            </motion.button>
                          );
                        }

                        // Show ellipsis for breaks in sequence
                        if (
                          (pageNumber === 2 && currentPage > 3) ||
                          (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                        ) {
                          return (
                            <span key={pageNumber} className="relative inline-flex items-center justify-center w-10 h-10">
                              <FaEllipsisH className="text-gray-400" />
                            </span>
                          );
                        }

                        return null;
                      })}
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-xl ${currentPage === totalPages
                          ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                          : 'text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                    >
                      Next
                      <FaChevronRight className="ml-2 h-4 w-4" />
                    </motion.button>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
            >
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Options</h2>

                {/* Tab Navigation */}
                <div className="flex border-b mb-6">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    className={`py-2 px-4 ${activeTab === "delete"
                      ? "border-b-2 border-red-500 text-red-500"
                      : "text-gray-500"}`}
                    onClick={() => setActiveTab("delete")}
                  >
                    Delete
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    className={`py-2 px-4 ${activeTab === "deactivate"
                      ? "border-b-2 border-yellow-500 text-yellow-500"
                      : "text-gray-500"}`}
                    onClick={() => setActiveTab("deactivate")}
                  >
                    Deactivate
                  </motion.button>
                  {customerStatus === "inactive" && (
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      className={`py-2 px-4 ${activeTab === "activate"
                        ? "border-b-2 border-green-500 text-green-500"
                        : "text-gray-500"}`}
                      onClick={() => setActiveTab("activate")}
                    >
                      Activate
                    </motion.button>
                  )}
                </div>

                {/* Tab Content */}
                <AnimatePresence mode="wait">
                  {activeTab === "delete" && (
                    <motion.div
                      key="delete"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <div className="bg-red-50 p-4 rounded-xl mb-4">
                        <p className="text-lg text-red-600 font-semibold">
                          Are you sure you want to delete this customer?
                        </p>
                        <p className="text-gray-700 mt-2 text-sm">
                          Deleting a customer is permanent and cannot be undone. This will remove all associated records including transaction history and account information.
                        </p>
                      </div>
                      <div className="flex justify-end gap-3 mt-6">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setIsModalOpen(false)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm"
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={deleteCustomer}
                          className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm flex items-center gap-2"
                        >
                          {loading ? (
                            <>
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                              />
                              Processing...
                            </>
                          ) : (
                            <>Delete Customer</>
                          )}
                        </motion.button>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === "deactivate" && (
                    <motion.div
                      key="deactivate"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <div className="bg-yellow-50 p-4 rounded-xl mb-4">
                        <p className="text-lg text-yellow-600 font-semibold">
                          Are you sure you want to deactivate this customer?
                        </p>
                        <p className="text-gray-700 mt-2 text-sm">
                          Deactivating a customer will make their account inactive. They will no longer have access to their account, but their records will remain in the system.
                        </p>
                      </div>
                      <div className="flex justify-end gap-3 mt-6">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setIsModalOpen(false)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm"
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={deactivateCustomer}
                          className="px-4 py-2 bg-yellow-500 text-white rounded-xl text-sm flex items-center gap-2"
                        >
                          {loading ? (
                            <>
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                              />
                              Processing...
                            </>
                          ) : (
                            <>Deactivate Customer</>
                          )}
                        </motion.button>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === "activate" && (
                    <motion.div
                      key="activate"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <div className="bg-green-50 p-4 rounded-xl mb-4">
                        <p className="text-lg text-green-600 font-semibold">
                          Do you want to activate this customer?
                        </p>
                        <p className="text-gray-700 mt-2 text-sm">
                          Activating this customer will restore their access to the system.
                        </p>
                      </div>
                      <div className="flex justify-end gap-3 mt-6">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setIsModalOpen(false)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm"
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={activateCustomerStatus}
                          className="px-4 py-2 bg-green-500 text-white rounded-xl text-sm flex items-center gap-2"
                        >
                          {loading ? (
                            <>
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                              />
                              Processing...
                            </>
                          ) : (
                            <>Activate Customer</>
                          )}
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default Customers;